import React, { useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Fullscreen lightbox for gallery images.
 * Props:
 *   images: [{ src }]
 *   index: current index (or null when closed)
 *   onClose(): close handler
 *   onNav(nextIndex): navigate to index
 */
export const Lightbox = ({ images, index, onClose, onNav }) => {
  const open = index !== null && index !== undefined;
  const current = open ? images[index] : null;

  const goPrev = useCallback(() => {
    if (!open) return;
    onNav((index - 1 + images.length) % images.length);
  }, [open, index, images, onNav]);

  const goNext = useCallback(() => {
    if (!open) return;
    onNav((index + 1) % images.length);
  }, [open, index, images, onNav]);

  // Keyboard nav + body scroll lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, goPrev, goNext, onClose]);

  return ReactDOM.createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-xl"
          onClick={onClose}
          data-testid="lightbox"
        >
          {/* Close */}
          <button
            aria-label="Close"
            data-testid="lightbox-close"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="absolute top-6 right-6 z-10 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-slate-900 grid place-items-center transition shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
          >
            <X size={20} />
          </button>

          {/* Counter */}
          <div className="absolute top-6 left-6 z-10 glass-strong rounded-full px-4 py-1.5 font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-800">
            {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </div>

          {/* Prev */}
          {images.length > 1 && (
            <button
              aria-label="Previous"
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/90 hover:bg-white text-slate-900 grid place-items-center transition shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
            >
              <ChevronLeft size={22} />
            </button>
          )}

          {/* Next */}
          {images.length > 1 && (
            <button
              aria-label="Next"
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/90 hover:bg-white text-slate-900 grid place-items-center transition shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
            >
              <ChevronRight size={22} />
            </button>
          )}

          {/* Image */}
          <div
            className="absolute inset-0 flex items-center justify-center p-6 md:p-16"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.img
              key={current?.src}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              src={current?.src}
              alt=""
              data-testid="lightbox-image"
              className="max-w-full max-h-full object-contain rounded-xl shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default Lightbox;
